import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import File from "@mapmycustomers/shared/types/File";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import { Dropdown, SearchField } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import { getFileTags } from "@app/store/fileTags";
import { RootState } from "@app/store/rootReducer";
import useSizeObserver from "@app/util/hook/useSizeObserver";

import styles from "./FileTagSelector.module.scss";
import TagItem from "./TagItem";

export interface FileTagSelectorProps {
  className?: string;
  onChange: (tags: File["tags"]) => void;
  value: File["tags"];
}

const FileTagSelector = ({
  className,
  fileTags,
  onChange,
  value,
}: FileTagSelectorProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const [width, , ref] = useSizeObserver<HTMLDivElement>();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>("");
  const recentlyUsedTagIds = localSettings.getFileTagsUsage();

  const selectedTagIds = useMemo(() => value?.map(({ id }) => id) ?? [], [value]);

  const filteredTags = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (
      search ? fileTags.filter(({ name }) => name.toLowerCase().includes(query)) : fileTags
    ).sort(nameComparator);
  }, [fileTags, search]);

  const handleChange = useCallback(
    (tag: FileTag) => {
      if (selectedTagIds.includes(tag.id)) {
        onChange((value ?? []).filter(({ id }) => id !== tag.id));
      } else {
        localSettings.updateFileTagsUsage(tag.id);
        onChange([...(value ?? []), tag]);
      }
    },
    [onChange, selectedTagIds, value]
  );

  const recentlyUsedTags = (
    !search ? fileTags.filter(({ id }) => recentlyUsedTagIds.includes(id)) : []
  ).sort(nameComparator);

  const handleSearch = useCallback(
    (query: string) => {
      setSearch(query);
      if (!open) {
        setOpen(true);
      }
    },
    [open]
  );

  return (
    <Dropdown
      dropdownRender={() => (
        <div className={styles.list} style={{ width }}>
          {filteredTags.length ? (
            <>
              {recentlyUsedTags.length > 0 && (
                <>
                  <span className={styles.subList}>
                    {intl.formatMessage({
                      id: "files.tags.selector.recentlyUsed",
                      defaultMessage: "Recently Used",
                      description: "Recently Used Tags label",
                    })}
                  </span>
                  {recentlyUsedTags.map((tag) => (
                    <TagItem
                      key={tag.id}
                      onClick={handleChange}
                      selected={selectedTagIds.includes(tag.id)}
                      tag={tag}
                    />
                  ))}
                </>
              )}
              <span className={styles.subList}>
                {intl.formatMessage({
                  id: "files.tags.selector.allTags",
                  defaultMessage: "All Tags",
                  description: "All Tags label",
                })}
              </span>
              {filteredTags.map((tag) => (
                <TagItem
                  key={tag.id}
                  onClick={handleChange}
                  selected={selectedTagIds.includes(tag.id)}
                  tag={tag}
                />
              ))}
            </>
          ) : (
            <span className={styles.noTags}>
              {intl.formatMessage({
                id: "files.tags.selector.noTags",
                defaultMessage: "No tags exist. Add a tag in settings.",
                description: "File tags selector no tags",
              })}
            </span>
          )}
        </div>
      )}
      onOpenChange={setOpen}
      open={open}
      placement="bottom"
      trigger={["click"]}
    >
      <div ref={ref}>
        <SearchField
          className={className}
          onChange={handleSearch}
          placeholder={intl.formatMessage({
            id: "files.tags.selector.placeholder",
            defaultMessage: "Search Tags",
            description: "File tags selector placeholder",
          })}
          size="middle"
          value={search}
        />
      </div>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  fileTags: getFileTags(state),
});

export default connect(mapStateToProps)(FileTagSelector);
